import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface CategoryHeroBannerStyle {
    bannerImageGridItem?: GridItemProps;
    bannerContentGridItem?: GridItemProps;
    button?: ButtonPresentationProps;
    bannerHeading?: InjectableCss;
    bannerSubHeading?: InjectableCss;
    bannerBrandHeading?: InjectableCss;
    bannerCategoryHeading?: InjectableCss;
}

export const categoryHeroBannerStyle: CategoryHeroBannerStyle = {
    bannerImageGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            [class^="LazyImageStyle-sc"] {
                border: 4px solid #e0dfe0;
                height: auto;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    css`
                        margin-right: 80px;
                    `,
                    css`
                        margin-right: 80px;
                    `,
                    css`
                        margin-right: 80px;
                    `,
                    css`
                        margin-right: 80px;
                    `,
                ])}
        `,
    },
    bannerContentGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            flex-direction: column;
        `,
    },
    bannerHeading: {
        css: css`
            margin-bottom: 24px;
        `,
    },
    bannerSubHeading: {
        css: css`
            margin-bottom: 24px;
            margin-top: 0;
            color: ${COLORS.background.tag};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: ${TYPOGRAPHY.size.large};
                    `,
                ])}
        `,
    },
    button: {
        mergeCss: true,
        css: css`
            min-height: 48px;
            span {
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                font-size: ${TYPOGRAPHY.size.large};
                line-height: 24px;
            }
        `,
    },
    bannerBrandHeading: {
        css: css`
            font-size: 20px !important;
            line-height: normal !important;
            text-transform: capitalize;
        `,
    },
    bannerCategoryHeading: {
        css: css`
            margin-top: 0px !important;
        `,
    },
};

const styles = categoryHeroBannerStyle;

const mapStateToProps = (state: ApplicationState) => ({
    breadCrumbs: state.components.breadcrumbs.links,
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & HasCategoryContext;

const CategoryHeroBanner = ({ category, breadCrumbs }: Props) => {
    const history = useHistory();

    if (!category || !category.properties.categoryCustomBanner) {
        return null;
    }

    const handler = (e: React.MouseEvent) => {
        history.push(category?.properties?.categoryBannerButtonUrl);
        e.preventDefault();
    };

    const categoryPath = category.path;

    const shouldDisplayBrand = categoryPath && categoryPath.toLowerCase()?.includes("/brands");

    const breadCrumbArray = breadCrumbs?.map(item => item.children);

    const brandName = breadCrumbArray?.[2];

    return (
        <GridContainer>
            <GridItem {...styles.bannerImageGridItem}>
                <LazyImage src={category?.properties?.categoryCustomBanner} altText={category?.properties?.categoryBannerHeading} />
            </GridItem>
            <GridItem {...styles.bannerContentGridItem}>
                {shouldDisplayBrand ? (
                    <>
                        <Typography variant="h1" {...styles.bannerHeading}> {category?.properties?.categoryBannerHeading} - {brandName}</Typography>
                           {/* <Typography variant="h2" {...styles.bannerCategoryHeading}>
                           
                        </Typography> */}
                    </>
                ) : (
                    <Typography variant="h1" {...styles.bannerHeading}>
                        {category?.properties?.categoryBannerHeading}
                    </Typography>
                )}
                <Typography variant="h5" {...styles.bannerSubHeading}>
                    {category?.properties?.categoryBannerSubHeading}
                </Typography>
                <StyledWrapper>
                    <Button {...styles.button} onClick={handler}>
                        {translate("{0}", category?.properties?.categoryBannerButtonText)}
                    </Button>
                </StyledWrapper>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withCategory(CategoryHeroBanner)),
    definition: {
        group: "Categories",
        icon: "image-landscape",
    },
};

export default widgetModule;
